import { callDelete, callPost } from '@curry-group/data-addons';
import { put, select, takeLatest } from 'redux-saga/effects';
import { api } from '../../api';
import { IMessageModel } from '../../../model/communication/Message';
import { IProfile } from '../../../model/profile';
import { ThingTypes } from '../../../model/ryve/Thing';
import { GetCookie } from '../../../helper';
import {
  sendBotChatMessage,
  addBotChatUserMessage,
  setBotChatSessionId,
  setBotChatWorking,
  botChatResponseEnd,
  saveBotChatRequest,
  saveBotChatSuccess,
  saveBotChatFailed,
  deleteBotChatRequest,
  deleteBotChatSuccess,
  deleteBotChatFailed,
  createComplaintRequest,
  createComplaintSuccess,
  createComplaintFailed,
} from '../../actions/botChat';
import { BOT_CREATED_BY } from '../../reducer/botChat';
import { subscribeToSignalR, unsubscribeFromSignalR, fetchCommunicationsRequest } from '../../reducer/communication';

// Single generic, user-facing error message. We never surface the real upstream
// error text or status code (e.g. 504) — that detail only lives in the backend logs.
const GENERIC_BOT_ERROR =
  'Es ist ein Fehler aufgetreten. Bitte wiederholen Sie die Anfrage nach ein paar Minuten nochmal. ' +
  'Sollte das Problem weiterhin bestehen, wenden Sie sich an info@medteconline.de.';

export function* botChatSaga() {
  yield takeLatest(sendBotChatMessage.type, sendBotChatMessageWorker);
  yield takeLatest(saveBotChatRequest.type, saveBotChatWorker);
  yield takeLatest(deleteBotChatRequest.type, deleteBotChatWorker);
  yield takeLatest(createComplaintRequest.type, createComplaintWorker);
}

function* createComplaintWorker(action: ReturnType<typeof createComplaintRequest>) {
  try {
    const xsrfToken = GetCookie('XSRF-TOKEN');
    const addHeader = xsrfToken ? { headers: { 'X-XSRF-TOKEN': xsrfToken } } : undefined;
    const result: { id?: string; alias?: string } = yield callPost(api.v2.complaint.create, { ...action.payload }, addHeader);
    if (result?.id) {
      yield put(createComplaintSuccess({ id: result.id, alias: result.alias }));
    } else {
      yield put(createComplaintFailed({}));
    }
  } catch (e: any) {
    console.error('Error in createComplaintWorker', e);
    yield put(createComplaintFailed({ error: e?.message || String(e) }));
  }
}

function* deleteBotChatWorker(action: ReturnType<typeof deleteBotChatRequest>) {
  const communicationId = action.payload.communicationId;
  try {
    const xsrfToken = GetCookie('XSRF-TOKEN');
    const addHeader = xsrfToken ? { headers: { 'X-XSRF-TOKEN': xsrfToken } } : undefined;

    const url = api.params(api.v2.botchatDelete, { communicationId });
    const result: any = yield callDelete(url, undefined, addHeader);

    if (result?.ok) {
      yield put(deleteBotChatSuccess({ communicationId }));
      const sidebarTypes: string[] | undefined = yield select(state => state.foundation?.activeConfig?.sidebarTypes);
      const inBotRubric: boolean = yield select(state => !!state.foundation?.activeConfig?.botChatEnabled);
      const types = sidebarTypes?.length ? sidebarTypes : inBotRubric ? [ThingTypes.OneToOne] : undefined;
      if (types && types.length > 0) {
        // Keep the bot/non-bot split on the refresh, otherwise the bot rubric would
        // reload with the regular 1:1 chats mixed in.
        yield put(fetchCommunicationsRequest({ types, botChats: inBotRubric ? 'only' : 'exclude' }));
      }
    } else {
      yield put(deleteBotChatFailed({ communicationId, error: result?.error || 'Delete failed' }));
    }
  } catch (e: any) {
    console.error('Error in deleteBotChatWorker', e);
    yield put(deleteBotChatFailed({ communicationId, error: e?.message || String(e) }));
  }
}

function buildUserMessage(content: string, profile: IProfile | undefined): IMessageModel {
  const now = Date.now();
  const userId = profile?.userId ?? '';
  return {
    id: 'user-' + now + '-' + Math.random().toString(36).slice(2, 8),
    parent: '',
    content,
    emotes: {},
    deleted: false,
    createdBy: userId,
    createdAt: now,
    modifiedAt: now,
    modifiedBy: userId,
    createdByResolved: profile
      ? ({
          _id: userId,
          type: ThingTypes.Profile,
          content: profile,
        } as any)
      : undefined,
  } as IMessageModel;
}

function* sendBotChatMessageWorker(action: ReturnType<typeof sendBotChatMessage>) {
  const profile: IProfile | undefined = yield select(state => state.foundation?.profile);
  const userId: string = profile?.userId ?? '';
  const sessionId: string | undefined = yield select(state => state.botChat?.sessionId);
  const savedCommunicationId: string | undefined = yield select(state => state.botChat?.savedCommunicationId);
  const messageContent = action.payload.content;
  const botCommId = 'no_comm_XB_ChatRequest_' + userId;

  const userMessage = buildUserMessage(messageContent, profile);
  yield put(addBotChatUserMessage({ message: userMessage }));
  yield put(setBotChatWorking({ working: true }));
  yield put(subscribeToSignalR({ id: botCommId }));

  try {
    const result: any = yield callPost(api.v2.botchat, {
      question: messageContent,
      sessionId: sessionId || undefined,
    });

    yield put(unsubscribeFromSignalR({ id: botCommId }));

    const finalSessionId: string = result?.session_id || sessionId || '';
    if (result?.session_id) {
      yield put(setBotChatSessionId({ sessionId: result.session_id }));
    }

    let botAnswerText: string;
    if (result?.bot_error) {
      console.error('Bot chat error:', result.bot_error);
      botAnswerText = GENERIC_BOT_ERROR;
      yield put(botChatResponseEnd({ response: botAnswerText, sessionId: finalSessionId }));
      return;
    }

    botAnswerText = result?.bot_answer || '';
    yield put(botChatResponseEnd({ response: botAnswerText, sessionId: finalSessionId }));

    // If we're inside a saved chat, persist the new turn (user question + bot answer)
    // server-side so it shows up on next reload.
    if (savedCommunicationId && botAnswerText) {
      yield* appendTurnToSavedChat(savedCommunicationId, finalSessionId, userMessage, botAnswerText);
    }
  } catch (e: any) {
    // Includes gateway timeouts (504) and any other transport error. Log the real
    // detail to the console only; show the user the single generic message.
    console.error('Error in sendBotChatMessageWorker', e);
    yield put(unsubscribeFromSignalR({ id: botCommId }));
    yield put(botChatResponseEnd({
      response: GENERIC_BOT_ERROR,
      sessionId: sessionId || '',
    }));
  }
}

function* appendTurnToSavedChat(
  communicationId: string,
  sessionId: string,
  userMessage: IMessageModel,
  botAnswerText: string
) {
  try {
    const xsrfToken = GetCookie('XSRF-TOKEN');
    const addHeader = xsrfToken ? { headers: { 'X-XSRF-TOKEN': xsrfToken } } : undefined;
    const botCreatedAt = Date.now();

    const result: any = yield callPost(
      api.v2.botchatAppend,
      {
        communicationId,
        sessionId,
        userMessage: { content: userMessage.content, createdAt: userMessage.createdAt },
        botMessage: { content: botAnswerText, createdAt: botCreatedAt },
      },
      addHeader
    );

    if (result?.ok) {
      // Refresh the sidebar so the chat sorts to top by LastActionTimestamp.
      const sidebarTypes: string[] | undefined = yield select(state => state.foundation?.activeConfig?.sidebarTypes);
      const inBotRubric: boolean = yield select(state => !!state.foundation?.activeConfig?.botChatEnabled);
      const types = sidebarTypes?.length ? sidebarTypes : inBotRubric ? [ThingTypes.OneToOne] : undefined;
      if (types && types.length > 0) {
        // Keep the bot/non-bot split on the refresh, otherwise the bot rubric would
        // reload with the regular 1:1 chats mixed in.
        yield put(fetchCommunicationsRequest({ types, botChats: inBotRubric ? 'only' : 'exclude' }));
      }
    } else {
      console.error('botchat/append did not return ok:', result);
    }
  } catch (e: any) {
    console.error('Error in appendTurnToSavedChat', e);
  }
}

function* saveBotChatWorker(action: ReturnType<typeof saveBotChatRequest>) {
  try {
    const sessionId: string | undefined = yield select(state => state.botChat?.sessionId);
    const messages: IMessageModel[] = yield select(state => state.botChat?.messages);

    const xsrfToken = GetCookie('XSRF-TOKEN');
    const addHeader = xsrfToken ? { headers: { 'X-XSRF-TOKEN': xsrfToken } } : undefined;

    const result: any = yield callPost(
      api.v2.botchatSave,
      {
        sessionId,
        title: action.payload.title,
        messages: (messages || []).map(m => ({
          content: m.content,
          createdBy: m.createdBy === BOT_CREATED_BY ? 'bot' : 'user',
          createdAt: m.createdAt,
        })),
      },
      addHeader
    );

    if (result?.communicationId) {
      yield put(saveBotChatSuccess({ communicationId: result.communicationId, alias: result.alias }));
      const sidebarTypes: string[] | undefined = yield select(state => state.foundation?.activeConfig?.sidebarTypes);
      const inBotRubric: boolean = yield select(state => !!state.foundation?.activeConfig?.botChatEnabled);
      const types = sidebarTypes?.length ? sidebarTypes : inBotRubric ? [ThingTypes.OneToOne] : undefined;
      if (types && types.length > 0) {
        // Keep the bot/non-bot split on the refresh, otherwise the bot rubric would
        // reload with the regular 1:1 chats mixed in.
        yield put(fetchCommunicationsRequest({ types, botChats: inBotRubric ? 'only' : 'exclude' }));
      }
    } else {
      yield put(saveBotChatFailed({ error: 'Save failed' }));
    }
  } catch (e: any) {
    console.error('Error in saveBotChatWorker', e);
    yield put(saveBotChatFailed({ error: e.message }));
  }
}
